import {
  Box,
  Button,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  InventoryProfileService,
  PaymentProfileService,
} from "subModule/src/core/services";
import {
  PurchaseOrderStatuses,
  PurchasePayments,
  SaveInvoiceResponse,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import BasicDatePicker from "common/Pickers/Pickers";
import dayjs from "dayjs";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
// import { ReactComponent as Completed } from "assets/completed.svg";
import { useGetPurchaseOrderStatusesService } from "subModule/src/services/inventory/useInventory";
import DynamicSelect from "common/Select/Select";
import CommonCheckbox from "common/CheckBox/Checkbox";
import { GetPaymentsCreditResponse } from "subModule/src/services/payment/interface";
import { useGetPaymentModesDropdownService } from "subModule/src/services/payment/usePayments";
import { useGetBankAccountsDropdownService } from "subModule/src/services/bankaccount/useBankAccount";
interface FormData {
  [key: string]: any;
}
interface PurchasePaymentProps {
  steps: string[];
  setCurrentSteps?: any;
}

const PurchasePayment: React.FC<PurchasePaymentProps> = ({
  steps,
  setCurrentSteps,
}) => {
  const params = useParams();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const supplierId = (location.state as { supplier?: number | null })?.supplier;
  const handleBack = () =>
    supplierId ? navigate(-1) : navigate("/inventory/purchase-orders");
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      paymentDate: dayjs(new Date())?.format("YYYY-MM-DD"),
      receiptDate: dayjs(new Date())?.format("YYYY-MM-DD"),
    },
  });
  const { data: poStatusesData, refetch: poStatusesRefetch } =
    useGetPurchaseOrderStatusesService(+(params as any)?.id);
  const [currentStep, setCurrentStep] = useState<number>(
    steps.indexOf("supplierPayment"),
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [invoice, setInvoice] = useState<SaveInvoiceResponse>();
  const [payment, setPayment] = useState<PurchasePayments>();
  const [checkStatus, setCheckStatus] = useState<number | null>(2);
  const [allStatuses, setAllStatuses] = useState<PurchaseOrderStatuses>({});
  const [credit, setCredit] = useState<number>(0);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: bankAccountsData, refetch: bankAccountsRefetch } =
    useGetBankAccountsDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      search: dropdownSearchTerm,
    });
  const { data: paymentModesData, refetch: paymentModesRefetch } =
    useGetPaymentModesDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  useEffect(() => {
    setTimeout(() => setLoading(true), 10);
    poStatusesRefetch();
    paymentModesRefetch();
    bankAccountsRefetch();
    if (params?.id) {
      getInvoiceById();
    }
  }, []);
  useEffect(() => {
    setAllStatuses(poStatusesData?.data || {});
  }, [poStatusesData]);
  const paymentModes = (paymentModesData?.pages[0]?.data?.content || []).map(
    (item?: any) => ({ value: item?.id, label: item?.name }),
  );
  const bankAccounts = (bankAccountsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; accountTitle: string }) => ({
      value: item.id,
      label: item.accountTitle,
    }),
  );
  const getInvoiceById = () => {
    InventoryProfileService.getInvoiceByPOID(params?.id)
      .then((res: SaveInvoiceResponse) => {
        if (res.data) {
          fetchCredit(res?.data?.supplierId || 0);
          if ((res.data as any)?.paymentStatus === 779)
            getPaymentsByInvoiceId(res?.data?.id || 0);
          setCheckStatus((res.data as any)?.paymentStatus === 779 ? 1 : 2);
          setInvoice(res.data as any);
          setValue("supplierId", {
            value: res.data?.supplierId,
            label: res.data?.vendorTitle,
          });
          setValue(
            "amount",
            res?.data?.balanceAmount
              ? res?.data?.balanceAmount
              : res?.data?.totalAmount,
          );
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const getPaymentsByInvoiceId = (invoiceId: number) => {
    InventoryProfileService.purchasePaymentsByInvoice(invoiceId)
      .then((res: any) => {
        if (res.success) {
          setLoading(false);
          fetchCredit(res?.data[0]?.supplierId || 0);
          reset({
            ...(res?.data[0] || {}),
            supplierId: {
              value: res.data[0]?.supplierId,
              label: res.data[0]?.supplierName,
            },
            paymentMode: res.data[0]?.paymentMode
              ? {
                  value: res.data[0]?.paymentMode,
                  label: res?.data[0]?.paymentModeTitle,
                }
              : null,
            bankAccountId: res.data[0]?.bankAccountId
              ? {
                  value: res.data[0]?.bankAccountId,
                  label:
                    res.data[0]?.accountTitle || res.data[0]?.bankAccountId,
                }
              : null,
          });
          setPayment({ ...(res?.data[0] || {}) });
        } else {
          setLoading(false);
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const fetchCredit = (supplierId: number) => {
    PaymentProfileService.getPaymentsCredit(
      supplierId,
      "purchasepayment/credit",
    )
      .then((response: GetPaymentsCreditResponse) => {
        if (response.success) {
          setCredit(response?.data || 0);
        } else {
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const onSubmit = (data: FormData) => {
    InventoryProfileService.createPurchasePayment({
      ...data,
      supplierId: data.supplierId.value,
      paymentMode: data?.paymentMode?.value || null,
      adjustmentType: 592,
      bankAccountId: data?.bankAccountId?.value || null,
      purchaseInvoices: [(invoice as any)?.id || invoice?.data?.id || 0],
    })
      .then((response: any) => {
        if (response.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                t("supplierPayment") +
                " " +
                t("recorded") +
                " " +
                t("successfully")
              }
            />,
            { variant: "success" },
          );
          if (currentStep + 1 === steps.indexOf("Stock Received")) {
            setCurrentStep(steps.indexOf("Stock Received"));
            setCurrentSteps(steps.indexOf("Stock Received"));
          } else {
            navigate(`/inventory/supplier-payment/${response.data.id}/print`);
          }
        } else {
          enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "accounts")
        bankAccountsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "paymentModes")
        paymentModesRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return loading ? (
    <Loader />
  ) : (
    <Grid
      container
      component="form"
      id="form"
      className="px-[8px] items-baseline"
      rowGap={2}
      columnGap={3}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container className="items-center">
        <Grid item xs={12} sm={6} className="text-start">
          <Typography
            className="font-Saira text-[15px] font-semibold"
            style={{ color: themeColors?.primary }}
          >
            {checkStatus === 1 ? t("supplierPayment") : t("newSupplierPayment")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-end">
            {checkStatus === 2 ? (
              <Button
                type="submit"
                className="primary"
                style={{ background: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => onSubmit(handleSubmit(onSubmit))}
              >
                {t("submit")}
              </Button>
            ) : null}
            {currentStep + 1 === steps.indexOf("Stock Received") ? (
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => {
                  setCurrentStep(steps.indexOf("Stock Received"));
                  setCurrentSteps(steps.indexOf("Stock Received"));
                }}
              >
                {t("Next")}
              </Button>
            ) : null}
            {/* {checkStatus === 1 ? <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation onClick={() => navigate(`${window.location.pathname}/print/invoice`)}>{t('Print')}</Button> :  null} */}
            <Button
              className="secondary"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={handleBack}
            >
              {t("Back to list")}
            </Button>
          </div>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider className="bg-[#AEB3C4] hide-on-print" />
      </Grid>
      {checkStatus === 1 ? (
        <Grid
          container
          className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
          style={{ background: themeColors?.primary }}
        >
          <Grid item xs={4}>
            <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
              {t("supplierPayment")}
            </Typography>
            <Typography className="font-Saira text-[13px] text-[#fff] text-left">
              <span className="font-bold">{t("Payment")} # </span>{" "}
              <span>{payment?.paymentNumber || ""}</span>
            </Typography>
            <Typography className="font-Saira text-[13px] text-[#fff] text-left">
              <span className="font-bold">
                {t("payment") + " " + t("Date")}
              </span>{" "}
              <span>{payment?.paymentDate?.split("T")[0] || ""}</span>
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <div className="flex justify-end gap-10">
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">
                  {t("Supplier")} {t("name")}
                </span>
                <br />
                <span className="font-medium">
                  {payment?.supplierName || ""}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("referenceNo")}</span> <br />
                <span className="font-medium">
                  {payment?.referenceNumber || ""}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("PaymentMode")}</span> <br />
                <span className="font-medium">
                  {payment?.paymentModeTitle || ""}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("recipentDate")}</span> <br />
                <span className="font-medium">
                  {payment?.receiptDate?.split("T")[0] || 0}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("balanceAmount")}</span> <br />
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {}).format(
                    payment?.balanceAmount || 0,
                  ) || 0}
                </span>
              </Typography>
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("amount")}</span> <br />
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {}).format(
                    payment?.amount || 0,
                  ) || 0}
                </span>
              </Typography>
            </div>
          </Grid>
        </Grid>
      ) : null}
      <Grid container>
        <Grid item xs={12}>
          <Stepper
            className="hide-on-print"
            activeStep={currentStep}
            connector={<></>}
            sx={{
              "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer":
                {
                  background: `linear-gradient(20.78deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
                },
            }}
          >
            {steps.map((label, index) => {
              const statusKeyMap: Record<string, keyof PurchaseOrderStatuses> =
                {
                  "Purchase Order": "purchaseOrder",
                  supplierBill: "purchaseInvoice",
                  "Stock Received": "stockReceive",
                  "Debit Note": "purchaseCreditDebitNote",
                  Unstock: "unStock",
                };
              const stepProps: { completed?: boolean } = {};
              const labelProps: { optional?: React.ReactNode } = {};
              const key = statusKeyMap[label];
              const isCompleted =
                allStatuses?.[key] === 2 ||
                allStatuses?.[key] === 3 ||
                allStatuses?.[key] === 4;
              const isSupplierBillCompleted =
                allStatuses?.purchaseInvoice === 2 ||
                allStatuses?.purchaseInvoice === 3 ||
                allStatuses?.purchaseInvoice === 4;
              return (
                <Step
                  key={index}
                  completed={isCompleted}
                  {...stepProps}
                  onClick={() => {
                    if (
                      document
                        .querySelector(`.MuiStep-root:nth-child(${index + 1})`)
                        ?.classList.contains("Mui-completed") ||
                      (isSupplierBillCompleted && label === "Stock Received")
                    ) {
                      setCurrentStep(steps.indexOf(label));
                      setCurrentSteps(steps.indexOf(label));
                    }
                  }}
                  className={`flex items-center ${document.querySelector(`.MuiStep-root:nth-child(${index + 1})`)?.classList.contains("Mui-completed") ? "cursor-pointer" : ""}`}
                >
                  {/* {document.querySelector(`.MuiStep-root:nth-child(${index + 1})`)?.classList.contains('Mui-completed') ? <Completed style={{ fill: themeColors?.primary, width: '15px', height: '15px' }} /> : null} */}
                  <StepLabel {...labelProps}>{t(label)}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Grid>
      </Grid>
      <Grid container item xs={12} gap={2}>
        <Grid item xs={12} md={3}>
          <Controller
            name="supplierId"
            control={control}
            defaultValue={null}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <DynamicSelect
                label={t("vendors")}
                {...field}
                options={[]}
                isSearchable
                isRequired
                error={errors.supplierId?.message as string}
                isDisabled={true}
              />
            )}
          />
        </Grid>
        {watch("useCredit") ? null : (
          <Grid item xs={12} md={3}>
            <Controller
              name="bankAccountId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("ACCOUNT") + " *"}
                  {...field}
                  options={bankAccounts}
                  isSearchable
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                  }}
                  error={errors.bankAccountId?.message as string}
                  isDisabled={checkStatus === 1 ? true : false}
                  isLoading={searchLoading === "accounts"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("accounts", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("accounts", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(bankAccountsData?.pages[0]?.data as any).last)
                      handleDropdownActions("accounts", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography
            className="font-Saira text-[13px] leading-[16px] font-semibold text-left"
            style={{ color: themeColors?.primary }}
          >
            {t("supplierPayment")} {t("Details")}
          </Typography>
        </Grid>
        {watch("useCredit") ? null : (
          <Grid item xs={12} md={3}>
            <Controller
              name="paymentMode"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("PaymentMode")}
                  {...field}
                  options={paymentModes}
                  isSearchable
                  isRequired
                  onChange={(selectedOption: any) =>
                    field.onChange(selectedOption)
                  }
                  error={errors.paymentMode?.message as string}
                  isDisabled={checkStatus === 1 ? true : false}
                  isLoading={searchLoading === "paymentModes"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("paymentModes", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("paymentModes", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(paymentModesData?.pages[0]?.data as any).last)
                      handleDropdownActions("paymentModes", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
        )}
        {watch("useCredit") ? null : (
          <Grid item xs={12} md={3}>
            <TextField
              type="number"
              inputProps={{ step: "any", min: 0 }}
              {...register("amount", {
                required: t("This field is required."),
                validate: (value) => value > 0 || t("amountGreaterThanZero"),
              })}
              placeholder={t("amount")}
              label={t("amount") + " *"}
              title={t("amount")}
              // onChange={(e: any) => {
              //     const value = e.target.value;
              //     e.target.value = value < 0 ? 0 : value || "";
              // }}
              fullWidth
              error={!!errors.amount}
              helperText={errors.amount?.message as string}
              InputLabelProps={{ shrink: true }}
              disabled={checkStatus === 1 ? true : false}
            />
          </Grid>
        )}
        <Grid item xs={12} md={3}>
          <TextField
            {...register("referenceNumber", {
              required: t("This field is required."),
            })}
            placeholder="REF-001"
            label={t("referenceNo") + " *"}
            title={t("referenceNo")}
            fullWidth
            error={!!errors.referenceNumber}
            helperText={errors.referenceNumber?.message as string}
            InputLabelProps={{ shrink: true }}
            disabled={checkStatus === 1 ? true : false}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Controller
            name="paymentDate"
            control={control}
            rules={{
              validate: (value) => {
                const date = new Date(value);
                return isNaN(date.getTime()) ? t("Invalid Date") : true;
              },
            }}
            render={({ field }) => (
              <BasicDatePicker
                type="datePicker"
                {...field}
                label={t("Payment") + " " + t("Date")}
                fullWidth
                onChange={(value: any) =>
                  field.onChange(
                    new Date(value)
                      .toLocaleDateString("en-CA", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                      ?.split("/")
                      .reverse()
                      .join("-"),
                  )
                }
                required={true}
                error={errors.paymentDate?.message as string}
                disable={checkStatus === 1 ? true : false}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Controller
            name="receiptDate"
            control={control}
            rules={{
              validate: (value) => {
                const date = new Date(value);
                return isNaN(date.getTime()) ? t("Invalid Date") : true;
              },
            }}
            render={({ field }) => (
              <BasicDatePicker
                type="datePicker"
                {...field}
                label={t("recipentDate")}
                fullWidth
                onChange={(value: any) =>
                  field.onChange(
                    new Date(value)
                      .toLocaleDateString("en-CA", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                      ?.split("/")
                      .reverse()
                      .join("-"),
                  )
                }
                required={true}
                error={errors.receiptDate?.message as string}
                disable={checkStatus === 1 ? true : false}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Controller
            name="useCredit"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Box sx={{ "& label": { width: "fit-content" } }}>
                <CommonCheckbox
                  {...field}
                  label={t("useCredit")}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event.target.checked);
                    setValue("paymentMode", null);
                    setValue("amount", 0);
                  }}
                  checked={field.value}
                  color={themeColors?.primary}
                  disabled={checkStatus === 1 ? true : false}
                />
              </Box>
            )}
          />
          {watch("supplierId") ? (
            <Typography className="font-Saira text-start font-[600] text-[17px] leading-[22px]">
              {t("creditAmount")} = {credit || 0}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            {...register("remarks")}
            label={t("remarks")}
            title={t("remarks")}
            placeholder={t("remarks")}
            fullWidth
            error={!!errors.remarks}
            helperText={errors.remarks?.message as string}
            InputLabelProps={{ shrink: true }}
            multiline
            rows={4}
            sx={{
              "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                width: "100%",
              },
            }}
            disabled={checkStatus === 1 ? true : false}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} className="hide-on-print">
        <Divider className="bg-[#AEB3C4] mt-[8px]" />
      </Grid>
      <Grid container className="items-center hide-on-print">
        <Grid item xs={12} sm={6} className="text-start"></Grid>
        <Grid item xs={12} sm={6}>
          <div className="flex gap-2 justify-end">
            {checkStatus === 2 ? (
              <Button
                type="submit"
                className="primary"
                style={{ background: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => onSubmit(handleSubmit(onSubmit))}
              >
                {t("save") + " " + t("Payment")}
              </Button>
            ) : null}
            {currentStep + 1 === steps.indexOf("Stock Received") ? (
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => {
                  setCurrentStep(steps.indexOf("Stock Received"));
                  setCurrentSteps(steps.indexOf("Stock Received"));
                }}
              >
                {t("skip")}
              </Button>
            ) : null}
            {/* {checkStatus === 1 ? <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation onClick={() => navigate(`${window.location.pathname}/print/invoice`)}>{t('Print')}</Button> : null} */}
            {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation onClick={() => navigate('/inventory')}>{t('Back to list')}</Button> */}
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PurchasePayment;
